/**
 * Company Type Definitions
 *
 * Defines business types for company classification and future personalization.
 * These types enable industry-specific features like default categories,
 * tailored reports, and relevant financial insights.
 */

/**
 * Company Type Enum
 *
 * 15 predefined business categories targeting small businesses in Brazil.
 * Values must match database CHECK constraint in companies table.
 */
export enum CompanyType {
  /** Agências (Marketing / Design / Software) */
  AGENCIES = 'agencies',

  /** Construção (Pequenas Obras / Reformas) */
  CONSTRUCTION = 'construction',

  /** Arquitetos e Engenheiros */
  ARCHITECTS_ENGINEERS = 'architects_engineers',

  /** Consultorias e Serviços B2B */
  CONSULTING_B2B = 'consulting_b2b',

  /** Clínicas de Saúde (Dentistas / Fisioterapeutas) */
  HEALTH_CLINICS = 'health_clinics',

  /** Advogados (Pequenos Escritórios) */
  LAW_FIRMS = 'law_firms',

  /** Produtoras de Eventos (Pequenas) */
  EVENT_PRODUCERS = 'event_producers',

  /** Empresas de TI (Suporte / MSP) */
  IT_COMPANIES = 'it_companies',

  /** Fotógrafos e Produtoras de Vídeo */
  PHOTOGRAPHERS_VIDEOGRAPHERS = 'photographers_videographers',

  /** Profissionais de Educação (Cursos Livres / Infoprodutores) */
  EDUCATION_PROFESSIONALS = 'education_professionals',

  /** Estúdios de Pilates / Yoga */
  PILATES_YOGA_STUDIOS = 'pilates_yoga_studios',

  /** Clínicas Veterinárias (Pequenas) */
  VETERINARY_CLINICS = 'veterinary_clinics',

  /** Clínicas de Estética */
  BEAUTY_CLINICS = 'beauty_clinics',

  /** Oficinas Mecânicas (Pequenas) */
  AUTO_REPAIR_SHOPS = 'auto_repair_shops',

  /** Salões de Beleza */
  BEAUTY_SALONS = 'beauty_salons',
}

/**
 * Human-readable labels for company types
 * Maps enum values to display text for UI components
 */
export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  [CompanyType.AGENCIES]: 'Agências (Marketing / Design / Software)',
  [CompanyType.CONSTRUCTION]: 'Construção (Pequenas Obras / Reformas)',
  [CompanyType.ARCHITECTS_ENGINEERS]: 'Arquitetos e Engenheiros',
  [CompanyType.CONSULTING_B2B]: 'Consultorias e Serviços B2B',
  [CompanyType.HEALTH_CLINICS]: 'Clínicas de Saúde (Dentistas / Fisioterapeutas)',
  [CompanyType.LAW_FIRMS]: 'Advogados (Pequenos Escritórios)',
  [CompanyType.EVENT_PRODUCERS]: 'Produtoras de Eventos (Pequenas)',
  [CompanyType.IT_COMPANIES]: 'Empresas de TI (Suporte / MSP)',
  [CompanyType.PHOTOGRAPHERS_VIDEOGRAPHERS]: 'Fotógrafos e Produtoras de Vídeo',
  [CompanyType.EDUCATION_PROFESSIONALS]: 'Profissionais de Educação (Cursos Livres / Infoprodutores)',
  [CompanyType.PILATES_YOGA_STUDIOS]: 'Estúdios de Pilates / Yoga',
  [CompanyType.VETERINARY_CLINICS]: 'Clínicas Veterinárias (Pequenas)',
  [CompanyType.BEAUTY_CLINICS]: 'Clínicas de Estética',
  [CompanyType.AUTO_REPAIR_SHOPS]: 'Oficinas Mecânicas (Pequenas)',
  [CompanyType.BEAUTY_SALONS]: 'Salões de Beleza',
};

/**
 * Company Interface
 * Represents a business entity in the system
 */
export interface Company {
  id: number | string;
  name: string;
  logo_url?: string | null;
  description?: string | null;
  company_type?: CompanyType | null; // Optional - can be null for existing companies
  created_at?: string | null;
  updated_at?: string | null;
  member_count?: number;
}

/**
 * Helper function to get company type label
 * Returns human-readable text or "Tipo não selecionado" for null values
 */
export function getCompanyTypeLabel(type: CompanyType | null | undefined): string {
  if (!type) return 'Tipo não selecionado';
  return COMPANY_TYPE_LABELS[type] || 'Tipo não selecionado';
}

/**
 * Helper function to get all company type options for Select components
 * Returns array of {value, label} objects
 */
export function getCompanyTypeOptions(): Array<{ value: CompanyType; label: string }> {
  return Object.values(CompanyType).map(value => ({
    value,
    label: COMPANY_TYPE_LABELS[value],
  }));
}
